<template>
  <div>
    <div v-if="isTeamLead && isDashboardPlusDenied">
      <div class="row m-4 mt-0">
        <div class="col-sm-12">
          <div class="card card-body mb-0 text-center text-muted py-3">
            {{ helper.trans('statistic.dashboardPlus.access_denied') }}
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="showTabs">
      <div class="row m-4 mt-0 mb-0">
        <div class="col-sm-12">
          <div class="card card-body mb-0">
            <div class="row">
              <div class="form-group col-sm-12">
                <ul class="navbar-nav overflow-auto flex-row gap-4" >
                  <li v-for="tab in availableTabs" :key="tab" class="nav-item nav-item-spaced d-lg-block text-nowrap" >
                    <a href="#" @click.prevent="selectTab(tab)" class="nav-link tab" :class="{'active': activeTabName === tab}">{{ helper.trans(`statistic.tab.${tab}`) }}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <StatisticSummary v-if="activeTabName === Tabs.summary && canShowSummary"></StatisticSummary>
        <StatisticTurnoverSummary v-if="activeTabName === Tabs.turnoverSummary && canShowTurnoverSummary"></StatisticTurnoverSummary>
        <StatisticOnline v-if="activeTabName === Tabs.online"></StatisticOnline>
        <StatisticRequisites v-if="activeTabName === Tabs.requisites"></StatisticRequisites>
        <StatisticLabelsOperations v-if="activeTabName === Tabs.labelsOperation && isAdmin"></StatisticLabelsOperations>
        <StatisticTraders v-if="activeTabName === Tabs.traders && isAdmin"></StatisticTraders>
        <StatisticDetailedAmounts v-if="activeTabName === Tabs.detailedAmounts"></StatisticDetailedAmounts>
        <StatisticPartnersIncomes v-if="activeTabName === Tabs.partnersIncomes"></StatisticPartnersIncomes>
        <StatisticProviderRequests v-if="activeTabName === Tabs.providerRequests"></StatisticProviderRequests>
        <StatisticIssuanceTime v-if="activeTabName === Tabs.issuanceTime"></StatisticIssuanceTime>
        <StatisticConversion v-if="activeTabName === Tabs.conversion"></StatisticConversion>
        <StatisticCascadeRating v-if="activeTabName === Tabs.cascadeRating && canShowCascadeRating"></StatisticCascadeRating>
        <StatisticSupportActivity v-if="activeTabName === Tabs.supportActivity"></StatisticSupportActivity>
        <StatisticDashboardPlus v-if="activeTabName === Tabs.dashboardPlus && canShowDashboardPlus"></StatisticDashboardPlus>
        <StatisticReceiptChecks v-if="activeTabName === Tabs.receiptChecks && canShowReceiptChecks"></StatisticReceiptChecks>
        <StatisticCounterparties v-if="activeTabName === Tabs.counterparties && canShowCounterparties"></StatisticCounterparties>
      </div>
    </div>
    <StatisticSummary v-else></StatisticSummary>
  </div>
</template>
<script lang="ts">
import {defineComponent, onMounted, toRefs, reactive, ref, computed, watchEffect} from 'vue'
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import StatisticSummary from "@/views/StatisticSummary.vue";
import StatisticTurnoverSummary from "@/views/StatisticTurnoverSummary.vue";
import StatisticOnline from "@/views/StatisticOnline.vue";
import StatisticLabelsOperations from "@/views/StatisticLabelsOperations.vue";
import StatisticRequisites from "@/views/StatisticRequisites.vue";
import StatisticTraders from "@/views/StatisticTraders.vue";
import {useRoute} from "vue-router";
import router from "@/router";
import StatisticPartnersIncomes from "@/views/StatisticPartnersIncomes.vue";
import StatisticDetailedAmounts from "@/views/StatisticDetailedAmounts.vue";
import StatisticProviderRequests from "@/views/StatisticProviderRequests.vue";
import StatisticConversion from "@/views/StatisticConversion.vue";
import StatisticIssuanceTime from "@/views/StatisticIssuanceTime.vue";
import StatisticCascadeRating from "@/views/StatisticCascadeRating.vue";
import StatisticSupportActivity from "@/views/StatisticSupportActivity.vue";
import StatisticDashboardPlus from "@/views/StatisticDashboardPlus.vue";
import StatisticReceiptChecks from "@/views/StatisticReceiptChecks.vue";
import StatisticCounterparties from "@/views/StatisticCounterparties.vue";
import {UserPermission} from "@/models/enum/UserPermission";
import {useCounterpartyManagementAccess} from "@/service/CounterpartyManagementAccess";

enum Tabs {
  summary = 'summary',
  turnoverSummary = 'turnoverSummary',
  online = 'online',
  requisites = 'requisites',
  labelsOperation = "labelsOperation",
  traders = 'traders',
  detailedAmounts = 'detailedAmounts',
  partnersIncomes = 'partnersIncomes',
  providerRequests = 'providerRequests',
  issuanceTime = 'issuanceTime',
  conversion = 'conversion',
  cascadeRating = 'cascadeRating',
  supportActivity = 'supportActivity',
  dashboardPlus = 'dashboardPlus',
  receiptChecks = 'receiptChecks',
  counterparties = 'counterparties',
}

export default defineComponent({
  components: {
    StatisticDetailedAmounts,
    StatisticTraders,
    StatisticRequisites,
    StatisticSummary,
    StatisticTurnoverSummary,
    StatisticOnline,
    StatisticLabelsOperations,
    StatisticPartnersIncomes,
    StatisticProviderRequests,
    StatisticConversion,
    StatisticIssuanceTime,
    StatisticCascadeRating,
    StatisticSupportActivity,
    StatisticDashboardPlus,
    StatisticReceiptChecks,
    StatisticCounterparties,
  },
  setup() {
    const route = useRoute()
    const store = useStore()
    const {canManageCounterparties} = useCounterpartyManagementAccess()
    const state = reactive({
      activeTabName: (route.params.tab ?? Tabs.summary) as Tabs,
    });

    const canShowSummary = computed(() => !store.getters.hasPermission(UserPermission.HIDE_STATISTICS_SUMMARY))
    const canShowTurnoverSummary = computed(() =>
      (!!store.getters.isAdmin || !!store.getters.isOperator)
      && !!store.getters.getAppData?.TURNOVER_SUMMARY_ENABLED
    )
    const canShowCascadeRating = computed(() =>
      !!store.getters.isAdmin
      && store.getters.hasPermission(UserPermission.EDIT_SETTINGS)
      && !!store.getters.getAppData?.providerRatingEnabled
    )
    const canShowDashboardPlus = computed(() =>
      !!store.getters.isAdmin
      || (!!store.getters.isTeamLead && store.getters.hasPermission(UserPermission.DASHBOARD_PLUS))
    )
    const canShowReceiptChecks = computed(() =>
      !!store.getters.isAdmin
      && !!store.getters.getAppData?.CHEQUE_WALES_CHECKER_ENABLED
    )
    const canShowCounterparties = computed(() => canManageCounterparties.value)
    const showTabs = computed(() =>
      !!store.getters.isAdmin
      || !!store.getters.isOperator
      || (!!store.getters.isTeamLead && store.getters.hasPermission(UserPermission.DASHBOARD_PLUS))
      || (canShowCounterparties.value && (
        !!store.getters.isTeamLead
        || !!store.getters.isTrader
        || !!store.getters.isTraderOperator
      ))
    )
    const isDashboardPlusDenied = computed(() =>
      !!store.getters.isTeamLead
      && !store.getters.hasPermission(UserPermission.DASHBOARD_PLUS)
      && state.activeTabName === Tabs.dashboardPlus
    )
    const withCounterpartyTabs = (tabs: string[]) => {
      if (!canShowCounterparties.value) {
        return tabs;
      }
      return [...tabs, Tabs.counterparties];
    }
    const availableTabs = computed(() => {
      if (store.getters.isTrader || store.getters.isTraderOperator) {
        const tabs = [] as string[];
        if (canShowSummary.value) {
          tabs.push(Tabs.summary);
        }
        return withCounterpartyTabs(tabs);
      }

      if (store.getters.isTeamLead && store.getters.hasPermission(UserPermission.DASHBOARD_PLUS)) {
        return withCounterpartyTabs([Tabs.summary, Tabs.dashboardPlus]);
      }

      if (store.getters.isTeamLead) {
        const tabs = [] as string[];
        if (canShowSummary.value) {
          tabs.push(Tabs.summary);
        }
        return withCounterpartyTabs(tabs);
      }

      let tabs = Object.values(Tabs) as string[];
      if (!canShowSummary.value) {
        tabs = tabs.filter((tab) => tab !== Tabs.summary);
      }
      if (!canShowCascadeRating.value) {
        tabs = tabs.filter((tab) => tab !== Tabs.cascadeRating);
      }
      if (!canShowTurnoverSummary.value) {
        tabs = tabs.filter((tab) => tab !== Tabs.turnoverSummary);
      }
      if (!canShowReceiptChecks.value) {
        tabs = tabs.filter((tab) => tab !== Tabs.receiptChecks);
      }
      if (!store.getters.isAdmin) {
        tabs = tabs.filter((tab) => tab !== Tabs.dashboardPlus);
      }
      if (!canShowCounterparties.value) {
        tabs = tabs.filter((tab) => tab !== Tabs.counterparties);
      }
      return tabs;
    })
    const selectTab = async (value: string) => {
      router.push(`/statistic/${value}`)
      state.activeTabName = value as Tabs
    }

    onMounted(() => {

    });

    watchEffect(() => {
      if (isDashboardPlusDenied.value) {
        return;
      }
      if (availableTabs.value.length === 0) {
        return;
      }
      if (!availableTabs.value.includes(state.activeTabName)) {
        state.activeTabName = availableTabs.value[0] as Tabs;
      }
    })

    return {
      ...toRefs(state),
      isOperator: computed(() => store.getters.isOperator),
      isAdmin: computed(() => store.getters.isAdmin),
      isTrader: computed(() => store.getters.isTrader),
      isTeamLead: computed(() => store.getters.isTeamLead),
      isReferral: computed(() => store.getters.isReferral),
      isMerchant: computed(() => store.getters.isMerchant),
      isMerchantOperator: computed(() => store.getters.isMerchantOperator),
      dateHelper,
      numberFormatter,
      helper,
      Tabs,
      selectTab,
      availableTabs,
      canShowSummary,
      canShowCascadeRating,
      canShowTurnoverSummary,
      canShowDashboardPlus,
      canShowReceiptChecks,
      showTabs,
      isDashboardPlusDenied,
      canShowCounterparties,
    }
  }
})
</script>
